import {ButtonBase, CircularProgress} from "@mui/material";
import {alpha, darken, useTheme} from "@mui/material/styles";
import React from "react";
import {signingControls} from "./signingColors";
import {controlRadius} from "./signingTheme";

interface Props {
    children: React.ReactNode;
    onClick?: () => void;
    type?: 'button' | 'submit';
    disabled?: boolean;
    busy?: boolean;
}

function SigningButton({children, onClick, type = 'button', disabled, busy}: Props) {
    const theme = useTheme();
    const controls = signingControls(theme);
    const pressed = darken(controls.fill, 0.12);
    const text = theme.palette.text.primary;

    return (
        <ButtonBase
            type={type}
            onClick={event => {
                if (busy) {
                    event.preventDefault();
                    return;
                }
                onClick?.();
            }}
            disabled={disabled && !busy}
            aria-disabled={busy || undefined}
            aria-busy={busy || undefined}
            sx={{
                ...theme.typography.button,
                position: 'relative',
                width: '100%',
                minHeight: 56,
                px: 7,
                borderRadius: `${controlRadius(theme)}px`,
                bgcolor: controls.fill,
                color: controls.onFill,
                fontSize: 17,
                transition: 'background-color 160ms cubic-bezier(0.22, 1, 0.36, 1)',
                ...(busy
                    ? {pointerEvents: 'none'}
                    : {'@media (hover: hover)': {'&:hover': {bgcolor: pressed}}, '&:active': {bgcolor: pressed}}),
                '&.Mui-focusVisible': {outline: `2px solid ${controls.focus}`, outlineOffset: 3},
                '&.Mui-disabled': {bgcolor: alpha(text, 0.09), color: alpha(text, 0.52)},
            }}
        >
            {busy && (
                <CircularProgress
                    size={20}
                    thickness={4.5}
                    color={'inherit'}
                    aria-hidden
                    sx={{position: 'absolute', left: 24}}
                />
            )}
            {children}
        </ButtonBase>
    );
}

export default SigningButton;
